import { StandardEvents } from '@shopify/events';

class ProductDescriptionExpand extends HTMLElement {
  /** @type {HTMLElement} */
  get #content() {
    return this.querySelector('.product-description-expand__content');
  }

  /** @type {HTMLButtonElement} */
  get #toggle() {
    return this.querySelector('.product-description-expand__toggle');
  }

  /** @type {HTMLElement} */
  get #toggleLabel() {
    return this.querySelector('.product-description-expand__toggle-label');
  }

  /** @type {HTMLElement} */
  get #toggleIcon() {
    return this.querySelector('.product-description-expand__toggle-icon');
  }

  #controller;

  /**
   * Initialises the component, hides the toggle if content fits within the collapsed height.
   * A ResizeObserver re-runs the check when the component width changes (e.g. orientation change).
   */
  connectedCallback() {
    this.#toggleIcon?.setAttribute('aria-hidden', 'true');
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const ro = new ResizeObserver(() => requestAnimationFrame(() => this.#checkOverflow()));
    ro.observe(this);
    signal.addEventListener('abort', () => ro.disconnect());

    requestAnimationFrame(() => this.#checkOverflow());
    this.#toggle?.addEventListener('click', this.#handleToggle, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });

    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
  }

  /**
   * Cleans up event listeners.
   */
  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Hides the toggle button when the full content already fits within the collapsed height.
   */
  #checkOverflow() {
    const content = this.#content;
    if (!content) return;

    const collapsedHeight = parseFloat(getComputedStyle(this).getPropertyValue('--collapsed-height')) || 100;

    const toggle = this.#toggle;
    if (content.scrollHeight <= collapsedHeight) {
      if (toggle) toggle.hidden = true;
      this.dataset.fits = 'true';
    } else {
      if (toggle) toggle.hidden = false;
      delete this.dataset.fits;
    }
  }

  /**
   * Applies collapsed ARIA/label/icon state — shared by #reset and #handleToggle.
   */
  #applyCollapsedState() {
    const toggle = this.#toggle;
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    const label = this.#toggleLabel;
    if (label) label.textContent = this.dataset.showMoreLabel;
    const icon = this.#toggleIcon;
    if (icon) icon.textContent = '+';
  }

  /**
   * Resets to the collapsed state without animation (called on variant change).
   */
  #reset() {
    const content = this.#content;
    if (content) content.style.maxHeight = '';
    this.removeAttribute('data-expanded');
    this.#applyCollapsedState();
  }

  /**
   * Resets expanded state and re-checks overflow after the DOM is morphed with new variant content.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        this.#reset();
        requestAnimationFrame(() => this.#checkOverflow());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-description-expand] Event promise rejected:', error);
      });
  };

  /**
   * Collapses the expanded description and returns focus to the toggle on Escape.
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    if (event.key === 'Escape' && this.hasAttribute('data-expanded')) {
      event.preventDefault();
      this.#handleToggle();
      this.#toggle?.focus();
    }
  };

  /**
   * Toggles between collapsed and expanded states, animating to/from the exact content height.
   */
  #handleToggle = () => {
    const isExpanded = this.hasAttribute('data-expanded');
    const content = this.#content;

    if (isExpanded) {
      // Pin to current rendered height so CSS can animate back to the collapsed height
      if (content) {
        content.style.maxHeight = content.offsetHeight + 'px';
        requestAnimationFrame(() => { content.style.maxHeight = ''; });
      }
      this.removeAttribute('data-expanded');
      this.#applyCollapsedState();
    } else {
      const toggle = this.#toggle;
      const label = this.#toggleLabel;
      const icon = this.#toggleIcon;
      this.setAttribute('data-expanded', '');
      toggle?.setAttribute('aria-expanded', 'true');
      if (label) label.textContent = this.dataset.showLessLabel;
      if (icon) icon.textContent = '−';
      // Animate to the exact content height rather than an arbitrary large value
      if (content) {
        content.style.maxHeight = content.scrollHeight + 'px';
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          content.style.maxHeight = 'none';
        } else {
          content.addEventListener('transitionend', () => {
            if (this.hasAttribute('data-expanded')) content.style.maxHeight = 'none';
          }, { once: true });
        }
      }
    }
  };
}

if (!customElements.get('product-description-expand')) {
  customElements.define('product-description-expand', ProductDescriptionExpand);
}
